import type { CollectionDetail } from '@recipes/contracts';
import {
  Alert,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  Spinner,
} from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { CollectionFormDialog } from '@/features/collections/collection-form-dialog';
import { SortableRecipeGrid } from '@/features/collections/sortable-recipe-grid';
import { ConfirmDialog } from '@/features/shared/confirm-dialog';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { RecipePickerDialog } from '@/features/shared/recipe-picker';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth/auth-provider';
import { describeError, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/feedback/toaster';

/**
 * Loose coupling to the offline manager owned by the core web agent: dispatching this
 * event asks it to download every recipe of the collection (with resized images) into
 * the Dexie store. The manager listens on window for 'rm:offline-download-collection'.
 */
export const OFFLINE_DOWNLOAD_COLLECTION_EVENT = 'rm:offline-download-collection';

export function CollectionDetailPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const key = queryKeys.collections.detail(id);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const query = useQuery({
    queryKey: key,
    queryFn: () => api.collections.get(id),
    enabled: Boolean(id),
  });
  const collection = query.data;
  const canEdit = Boolean(
    collection && (collection.ownerId === user?.id || collection.householdId),
  );

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: key });
    void queryClient.invalidateQueries({ queryKey: queryKeys.collections.all });
  };
  const addRecipes = useMutation({
    mutationFn: (recipeIds: string[]) => api.collections.addRecipes(id, { recipeIds }),
    onSuccess: (detail) => {
      queryClient.setQueryData(key, detail);
      setAddOpen(false);
      invalidate();
    },
    onError: (e) => toastError(e),
  });
  const removeRecipe = useMutation({
    mutationFn: (recipeId: string) => api.collections.removeRecipes(id, { recipeIds: [recipeId] }),
    onSuccess: (detail) => {
      queryClient.setQueryData(key, detail);
      invalidate();
    },
    onError: (e) => toastError(e),
  });
  const reorder = useMutation({
    mutationFn: (recipeIds: string[]) => api.collections.reorder(id, recipeIds),
    onMutate: async (recipeIds) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<CollectionDetail>(key);
      if (previous) {
        const byId = new Map(previous.recipes.map((r) => [r.id, r]));
        queryClient.setQueryData<CollectionDetail>(key, {
          ...previous,
          recipes: recipeIds.map((rid, i) => ({
            ...(byId.get(rid) as CollectionDetail['recipes'][number]),
            position: i,
          })),
        });
      }
      return { previous };
    },
    onError: (e, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(key, ctx.previous);
      toastError(e);
    },
    onSuccess: (detail) => queryClient.setQueryData(key, detail),
  });
  const remove = useMutation({
    mutationFn: () => api.collections.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.collections.all });
      navigate('/collections', { replace: true });
    },
    onError: (e) => toastError(e),
  });

  const downloadOffline = () => {
    window.dispatchEvent(
      new CustomEvent(OFFLINE_DOWNLOAD_COLLECTION_EVENT, { detail: { collectionId: id } }),
    );
    toast.success(t('collections.offlineRequested'));
  };

  if (query.isLoading)
    return (
      <PageContainer>
        <Spinner label={t('app.loading')} />
      </PageContainer>
    );
  if (!collection)
    return (
      <PageContainer>
        <Alert variant="destructive">{describeError(query.error)}</Alert>
      </PageContainer>
    );

  const sorted = [...collection.recipes].sort((a, b) => a.position - b.position);

  return (
    <PageContainer>
      <FeaturePageHeader
        title={collection.name}
        description={
          collection.description ?? t('collections.recipeCount', { count: collection.recipeCount })
        }
        backTo="/collections"
        actions={
          <>
            <Button variant="outline" onClick={downloadOffline}>
              <Download aria-hidden /> {t('collections.downloadOffline')}
            </Button>
            {canEdit && (
              <Button onClick={() => setAddOpen(true)} data-testid="collection-add-recipes">
                <Plus aria-hidden /> {t('collections.addRecipes')}
              </Button>
            )}
            {canEdit && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label={t('common.edit')}>
                    <MoreHorizontal aria-hidden />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => setEditOpen(true)}>
                    <Pencil aria-hidden /> {t('common.edit')}
                  </DropdownMenuItem>
                  <DropdownMenuItem destructive onSelect={() => setDeleteOpen(true)}>
                    <Trash2 aria-hidden /> {t('common.delete')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </>
        }
      />
      {sorted.length === 0 ? (
        <EmptyState
          title={t('collections.noRecipesTitle')}
          description={t('collections.noRecipesBody')}
          action={
            canEdit ? (
              <Button onClick={() => setAddOpen(true)}>{t('collections.addRecipes')}</Button>
            ) : undefined
          }
        />
      ) : (
        <>
          {canEdit && (
            <p className="mb-2 text-xs text-muted-foreground">{t('collections.dnd.hint')}</p>
          )}
          <SortableRecipeGrid
            recipes={sorted}
            canEdit={canEdit}
            onReorder={(ids) => reorder.mutate(ids)}
            onRemove={(rid) => removeRecipe.mutate(rid)}
          />
        </>
      )}
      <CollectionFormDialog open={editOpen} onOpenChange={setEditOpen} collection={collection} />
      <RecipePickerDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        excludeIds={sorted.map((r) => r.id)}
        loading={addRecipes.isPending}
        onConfirm={(recipes) => addRecipes.mutate(recipes.map((r) => r.id))}
      />
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t('collections.deleteTitle')}
        description={t('collections.deleteBody', { name: collection.name })}
        destructive
        confirmLabel={t('common.delete')}
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
    </PageContainer>
  );
}
